import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { Bootstrap } from '@/lib/types';

interface PortfolioState {
  data: Bootstrap | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Loads the whole public site in one request and applies the accent colour
 * stored in settings before first paint of the content.
 */
export function usePortfolio(): PortfolioState {
  const [data, setData] = useState<Bootstrap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .get<Bootstrap>('/bootstrap')
      .then((payload) => {
        if (cancelled) return;

        const accent = payload.settings.find((setting) => setting.key === 'site.accent')?.value;
        if (accent && /^\d{1,3} \d{1,3} \d{1,3}$/.test(accent)) {
          document.documentElement.style.setProperty('--c-accent', accent);
        }

        setData(payload);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return { data, loading, error, reload };
}
